import { logger } from '@lib/logger';
import type { AccountCaregiverEntryDto, AccountOfficeDto } from '@therame/shared';

/**
 * The context of this tab (#1822, #1841): the Active office every request names
 * in `X-Therame-Office-Id`, and the caregiver entry it acts through in
 * `X-Therame-Caregiver-Id`, if any. Held per tab, so two tabs can act in two
 * contexts; the last-used one is kept per Account and is the default at the
 * next login. Nothing is stored on the server.
 */
const ACTIVE_OFFICE = 'active_office_id';
const ACTIVE_CAREGIVER = 'active_caregiver_id';
const lastUsedOffice = (sub: string) => `last_office_id:${sub}`;
const lastUsedCaregiver = (sub: string) => `last_caregiver_id:${sub}`;
/** When the tab last started over after a refused office; guards against a loop. */
const OFFICE_RESTART_AT = 'office_restart_at';
const RESTART_GUARD_MS = 10_000;

/** Set while the „Praxis wählen“ page is up: no request names an office then. */
let choosing = false;

/** Whom a tab acts as: its own Person in the office, or a patient through an entry. */
export interface TabContext {
  officeId: string;
  caregiverId: string | null;
}

export const getActiveOfficeId = (): string | null => sessionStorage.getItem(ACTIVE_OFFICE);

export const getActiveCaregiverId = (): string | null => sessionStorage.getItem(ACTIVE_CAREGIVER);

const entriesOf = (office: AccountOfficeDto): AccountCaregiverEntryDto[] =>
  office.caregiverEntries ?? [];

/**
 * Every context of the Account, grouped by office: the own Person first, then
 * the patients it acts for there. An office with only entries has only those.
 */
export const contextsOf = (offices: AccountOfficeDto[]): TabContext[] =>
  offices.flatMap((office) => [
    ...(office.role ? [{ officeId: office.id, caregiverId: null }] : []),
    ...entriesOf(office).map((entry) => ({ officeId: office.id, caregiverId: entry.id })),
  ]);

/** The entry the tab acts through, as `/auth/offices` lists it; null for „Ich“. */
export const activeCaregiverEntry = (
  offices: AccountOfficeDto[],
): AccountCaregiverEntryDto | null => {
  const caregiverId = getActiveCaregiverId();
  if (!caregiverId) return null;
  const office = offices.find((candidate) => candidate.id === getActiveOfficeId());
  return (office && entriesOf(office).find((entry) => entry.id === caregiverId)) ?? null;
};

/** Whether the tab acts in this context. */
export const isActiveContext = (context: TabContext): boolean =>
  context.officeId === getActiveOfficeId() && context.caregiverId === getActiveCaregiverId();

/** The Account's contexts other than this tab's. */
export const otherContextsThan = (offices: AccountOfficeDto[]): TabContext[] =>
  contextsOf(offices).filter((context) => !isActiveContext(context));

const setOrRemove = (storage: Storage, key: string, value: string | null) => {
  if (value) storage.setItem(key, value);
  else storage.removeItem(key);
};

const remember = (sub: string, context: TabContext) => {
  sessionStorage.setItem(ACTIVE_OFFICE, context.officeId);
  setOrRemove(sessionStorage, ACTIVE_CAREGIVER, context.caregiverId);
  localStorage.setItem(lastUsedOffice(sub), context.officeId);
  setOrRemove(localStorage, lastUsedCaregiver(sub), context.caregiverId);
};

const forget = (sub: string | undefined) => {
  sessionStorage.removeItem(ACTIVE_OFFICE);
  sessionStorage.removeItem(ACTIVE_CAREGIVER);
  if (sub) {
    localStorage.removeItem(lastUsedOffice(sub));
    localStorage.removeItem(lastUsedCaregiver(sub));
  }
};

/**
 * A link that names its office (`?officeId=`: a push, or a call join request
 * from another office) opens the tab in that office (#1837), and in the entry
 * it names (`?caregiverId=`, #1841), else as the own Person. It becomes the
 * tab's choice, which {@link chooseOffice} keeps only while the Account still
 * has that context. The parameters leave the address, so the router never sees them.
 */
export const adoptOfficeFromUrl = () => {
  const url = new URL(window.location.href);
  const officeId = url.searchParams.get('officeId');
  if (!officeId) return;
  sessionStorage.setItem(ACTIVE_OFFICE, officeId);
  setOrRemove(sessionStorage, ACTIVE_CAREGIVER, url.searchParams.get('caregiverId'));
  url.searchParams.delete('officeId');
  url.searchParams.delete('caregiverId');
  window.history.replaceState(window.history.state, '', url.toString());
};

/** The address that opens `path` in `officeId` in a tab of its own (#1837). */
export const urlInOffice = (path: string, officeId: string): string => {
  const url = new URL(path, window.location.origin);
  url.searchParams.set('officeId', officeId);
  return url.toString();
};

export type OfficeChoice =
  | ({ kind: 'office' } & TabContext)
  /** The Account has no office yet (or only an officeless Person): no header, as before. */
  | { kind: 'none' }
  /** Several contexts and no valid stored choice: „Praxis wählen“. */
  | { kind: 'pick' };

/**
 * Picks the tab's context from the Account's offices, as after login: the tab's
 * own choice if still listed, else the last-used one, else the only one. Own
 * Persons and caregiver entries count alike (#1841). Remembers what it picked.
 */
export const chooseOffice = (sub: string, offices: AccountOfficeDto[]): OfficeChoice => {
  const contexts = contextsOf(offices);
  const listed = (officeId: string | null, caregiverId: string | null): TabContext | null =>
    contexts.find(
      (context) => context.officeId === officeId && context.caregiverId === caregiverId,
    ) ?? null;
  const context =
    listed(getActiveOfficeId(), getActiveCaregiverId()) ??
    listed(
      localStorage.getItem(lastUsedOffice(sub)),
      localStorage.getItem(lastUsedCaregiver(sub)),
    ) ??
    (contexts.length === 1 ? contexts[0] : null);

  if (context) {
    remember(sub, context);
    choosing = false;
    return { kind: 'office', ...context };
  }
  forget(sub);
  choosing = contexts.length > 1;
  return choosing ? { kind: 'pick' } : { kind: 'none' };
};

/**
 * Switches this tab to another context: remembers it and reloads the app, which
 * resets the query cache, stores, sockets and feature toggles in one go. The
 * app starts at `path`, home unless the caller needs a page of the new context.
 */
export const switchOffice = (
  sub: string,
  officeId: string,
  caregiverId: string | null = null,
  path = '/',
) => {
  remember(sub, { officeId, caregiverId });
  window.location.assign(path);
};

/**
 * The tab's context is gone for good (its access given up, #1842): forget it,
 * here and as the last-used one, and start over as after login, which goes
 * into the only context left or asks.
 */
export const leaveContext = (sub: string | undefined) => {
  forget(sub);
  window.location.assign('/');
};

/**
 * The backend refused the tab's office or caregiver entry, or asked for a
 * choice: drop both stored choices and start over as after login. A second
 * refusal right after a restart is left to surface as an error instead of
 * reloading forever.
 */
export const restartAfterOfficeRefusal = (sub: string | undefined) => {
  if (choosing) return;
  const lastRestart = Number(sessionStorage.getItem(OFFICE_RESTART_AT) ?? 0);
  if (Date.now() - lastRestart < RESTART_GUARD_MS) {
    logger.warn({ msg: 'Office refused again right after starting over; not reloading' });
    return;
  }
  sessionStorage.setItem(OFFICE_RESTART_AT, String(Date.now()));
  forget(sub);
  window.location.assign('/');
};
